import { broadcastCurrentUser, configure, getTokenSilently, loginWithRedirect } from "@sage/sbc.core.authentication.ui";
import { cleanup as cleanupScene, configure as configureScene } from "@sage/sbc.core.scene.ui";
import { createConfig, createLogger } from "@sage/sbc.core.utils.ui";
import { createBrowserHistory } from "history";
import { addErrorHandler, getAppStatus, registerApplication, start as startSingleSpa } from "single-spa";
import globalConfig from "../.config/global-config-const.json";
import { setAppName } from "../.config/newRelic/browserApi";
import { initializeNewRelic } from "../.config/newRelic/rootAgent";
import { Constants, QUERY_PATTERN } from "./sbc/constants";
import "./sbc/root-config-helper";
import { clearDbError, deleteIndexDbKeys } from "./sbc/utils/deleteIndexDbKeys";
import {
  shouldLoadDatalayer,
  shouldShowGlobalNav,
  shouldShowSpayAdvRegistrationPending,
  shouldShowSpayAdvWebApp
} from "./single-spa-config/ActivityFns";
import { pathname } from "./single-spa-config/helpers";

initializeNewRelic();
setAppName("root-config");

const { config } = createConfig();
const logger = createLogger();
const history = createBrowserHistory();

//const rootpath = () => (location: Location) => location.pathname === "/";
// const pathname = (str: string) => (location: Location) => location.pathname.startsWith(str);
// const strictPathname = (str: string) => (location: Location) =>
//   location.pathname.startsWith(`${str}/`) || location.pathname === str;

configureScene(history);

const clearUserDataCallback = async () => {
  deleteIndexDbKeys(QUERY_PATTERN);
  // Clear local storage, excluding import map overrides where applicable
  if (config("features.sbc.core.devtools.ui.enabled")) {
    const preserve: Record<string, string> = {};
    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith("import-map-override") ||
        [
          Constants.GLOBAL_CONFIG_OVERRIDES,
          Constants.GLOBAL_HANDLER_DEFINITIONS,
          Constants.GLOBAL_PERMISSIONS_OVERRIDES,
          Constants.GLOBAL_FEATURES_OVERRIDES,
          Constants.GLOBAL_MOCK_WORKFLOW_NOTIFICATIONS
        ].includes(key)
      ) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          preserve[key] = value;
        }
      }
    });

    localStorage.clear();

    Object.keys(preserve).forEach((key) => {
      localStorage.setItem(key, preserve[key]);
    });
  } else {
    localStorage.clear();
  }

  // Clear session storage
  sessionStorage.clear();

  // Clear service worker cache
  const response = new Promise((resolve: (value?: unknown) => void) => {
    navigator.serviceWorker.onmessage = (event) => {
      if (event.data) {
        const message = JSON.parse(event.data);
        if (message.type === "CACHE_INVALIDATED") {
          resolve();
        }
      }
    };
  });

  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    try {
      await navigator.serviceWorker.ready.then((registration) => {
        (registration.active as ServiceWorker).postMessage("INVALIDATE_CACHE");
      });

      await response;
    } catch (error) {
      logger.error(error);
    }
  }
  cleanupScene();
};

window.addEventListener("storage", (event) => {
  if (event.storageArea != localStorage) return;
  if (event.key === "clearUserData") {
    (async () => {
      await clearUserDataCallback();
      if (!pathname("/logout")(location) && !pathname("/logged-out")(location)) {
        window.location.reload();
      }
    })();
  }
});

// window.addEventListener("single-spa:before-routing-event", (event) => {
//   const { oldUrl, newUrl } = (event as CustomEvent).detail;

//   const urlsToHideNavBar = [
//     "/desktop/copilot",
//     "/cloudid/callback",
//     "/vscode/copilot",
//   ];
//   const navigationBar = document.getElementById("nav");

//   if (!navigationBar) {
//     return;
//   }

//   /**
//    * if new url and old url do not include one of the values in urlsToHideNavBar then return null
//    */
//   if (
//     !urlsToHideNavBar.some((url) => newUrl.includes(url)) &&
//     !urlsToHideNavBar.some((url) => oldUrl.includes(url))
//   ) {
//     return null;
//   }

//   if (urlsToHideNavBar.some((url) => newUrl.includes(url))) {
//     navigationBar.style.display = "none";
//   } else {
//     navigationBar.style.display = "";
//     !navigationBar.classList.contains("nav-styling") &&
//       navigationBar.classList.add("nav-styling");
//   }
// });

const activityFns = {
  "spa-app": pathname("/app"),
  "spa-onboarding": pathname("/registration-pending"),
  welcome: pathname("/welcome"),
  notifications: pathname("/notifications"),
  "global-pages": pathname("/error"),
  "client-management": pathname("/client-management"),
  manageProfile: pathname("/settings/profile-management"),
  manageBusiness: pathname("/settings/business-management"),
  "manage-business-secondary-nav": pathname("/settings/business-management"),
  manageUser: pathname("/settings/user-management"),
  "user-management-secondary-nav": pathname("/settings/user-management"),
  invitations: pathname("/invitations"),
  paymentPageCallback: pathname("/payment-page/callback"),
  callback: pathname("/cloudid/callback"),
  logout: pathname("/logout"),
  "logged-out": pathname("/logged-out"),
  home: pathname("/home"),
  "partnership-tax": pathname("/partnership-tax/"),
  "entity-list": pathname("/client-management/work-view"),
  "desktop-copilot": pathname("/desktop/copilot"),
  "account-management": pathname("/settings/account-management"),
  "vscode-copilot": pathname("/vscode/copilot")
} as Record<string, (location: Location) => boolean>;

if (config("features.sbc.core.root-config.ui.indexedDbFixEnabled")) {
  clearDbError();
}

configure({
  ...config("cloudid"),
  useRefreshTokens: true,
  useRefreshTokensFallback: true,
  clearUserDataCallback: clearUserDataCallback
});

configureScene(history);

if (config("features.sbc.core.authentication.ui.shouldCheckCloudIdOnPageLoad")) {
  const doNotAuthenticateUrls = [
    "/invitations",
    "/cloudid/callback",
    "/accounts/invoicing/preview",
    "/accounts/invoicing/payment",
    "/payment-page/callback",
    "/logout",
    "/logged-out"
  ];

  if (!doNotAuthenticateUrls.some((url) => window.location.pathname.startsWith(url))) {
    (async () => {
      try {
        await getTokenSilently();
      } catch (error) {
        if (error && typeof error === "object" && "error" in error && error.error === "login_required") {
          loginWithRedirect();
        }
      }
    })();
  }
}

if (window.location.pathname === "/") {
  history.push("/app" + window.location.search);
}

try {
  if (!activityFns.callback(window.location)) {
    // When in a CloudID callback, the CloudID callback page will initialise the current user
    // When not on a CloudID callback, we need to initialise the current user
    broadcastCurrentUser();
  }

  const props = {
    history,
    domElementGetter: () => {
      return document.getElementById("app");
    }
  };

  registerApplication(
    "sbc.core.errors.ui-error",
    () => System.import("sbc.core.errors.ui/index.js").then(({ ErrorPageLifecycles }) => ErrorPageLifecycles),
    activityFns["global-pages"],
    props
  );

  registerApplication(
    "sbc.core.globalnav.ui-simplenavbar",
    () => System.import("sbc.core.globalnav.ui/index.js").then(({ SimpleNavBarLifecycles }) => SimpleNavBarLifecycles),
    activityFns["global-pages"],
    {
      ...props,
      domElementGetter: () => {
        return document.getElementById("nav");
      }
    }
  );

  registerApplication(
    "sbc.core.errors.ui-404",
    () => System.import("sbc.core.errors.ui/index.js").then(({ Page404Lifecycles }) => Page404Lifecycles),
    (location) => !Object.keys(activityFns).some((key) => activityFns[key](location)),
    props
  );

  // registerApplication(
  //   "sbc.core.manage-profile.ui",
  //   () => System.import("sbc.core.manage-profile.ui/index.js"),
  //   activityFns["manageProfile"],
  //   props
  // );

  // registerApplication(
  //   "sbc.core.manage-business.ui",
  //   () => System.import("sbc.core.manage-business.ui/index.js"),
  //   (location) => activityFns["manageBusiness"](location) || activityFns["paymentPageCallback"](location),
  //   props
  // );

  registerApplication(
    "sbc.core.manage-business.ui-application-nav",
    () =>
      System.import("sbc.core.manage-business.ui/index.js").then(
        ({ ApplicationNavLifecycles }) => ApplicationNavLifecycles
      ),
    activityFns["manage-business-secondary-nav"],
    {
      ...props,
      domElementGetter: () => {
        return document.getElementById("secondary-nav");
      }
    }
  );

  registerApplication(
    "sbc.core.globalnav.ui",
    () => System.import("sbc.core.globalnav.ui/index.js").then(({ NavBarLifecycles }) => NavBarLifecycles),
    (location) => !pathname("/accounting/quickstart")(location) && shouldShowGlobalNav(location),
    {
      ...props,
      domElementGetter: () => {
        return document.getElementById("nav");
      }
    }
  );

  registerApplication(
    "sbc.core.authentication.ui-callback",
    () =>
      System.import("@sage/sbc.core.authentication.ui/index.js").then(
        ({ CloudIDCallbackLifeCycles }) => CloudIDCallbackLifeCycles
      ),
    activityFns["callback"],
    props
  );

  registerApplication(
    "sbc.core.globalnav.ui-logout",
    () => System.import("sbc.core.globalnav.ui/index.js").then(({ LogoutPageLifecycles }) => LogoutPageLifecycles),
    activityFns["logout"],
    props
  );

  registerApplication(
    "sbc.core.globalnav.ui-logged-out",
    () =>
      System.import("sbc.core.globalnav.ui/index.js").then(({ LoggedOutPageLifecycles }) => LoggedOutPageLifecycles),
    activityFns["logged-out"],
    props
  );

  // registerApplication(
  //   "datalayer",
  //   () =>
  //     System.import("sbc.core.datalayer.ui/index.js").then(() => {
  //       return {
  //         bootstrap: () => Promise.resolve(),
  //         mount: () => Promise.resolve(),
  //         unmount: () => Promise.resolve()
  //       };
  //     }),
  //   shouldLoadDatalayer,
  //   { history }
  // );

  registerApplication(
    "datalayer",
    () =>
      System.import("sbc.core.datalayer.ui/index.js").then(() => {
        return {
          bootstrap: () => Promise.resolve(),
          mount: () => Promise.resolve(),
          unmount: () => Promise.resolve()
        };
      }),
    shouldLoadDatalayer,
    { history }
  );

  registerApplication(
    globalConfig.spa_web_app_mfe_name_key,
    () =>
      System.import(globalConfig.spa_web_app_mfe_name_key).then(({ bootstrap, mount, unmount }) => ({
        bootstrap,
        mount,
        unmount
      })),
    shouldShowSpayAdvWebApp,
    props
  );

  // registerApplication(
  //   globalConfig.spa_registration_mfe_name_key,
  //   () => System.import(globalConfig.spa_registration_mfe_name_key).then(({ bootstrap, mount, unmount }) => ({
  //     bootstrap,
  //     mount,
  //     unmount
  //   })),
  //   shouldShowSpayAdvRegistration,
  //   props
  // );

  registerApplication(
    globalConfig.spa_onboarding_mfe_name_key,
    () => System.import(globalConfig.spa_onboarding_mfe_name_key),
    shouldShowSpayAdvRegistrationPending,
    props
  );

  // registerApplication({
  //   name: "sbc.core.home.ui",
  //   app: () => System.import("sbc.core.home.ui").then((module) => module.default), // No need for /index.js
  //   activeWhen: activityFns.home,
  //   customProps: props
  // });

  addErrorHandler((err) => {
    console.log(err.message);
    console.log(err.cause);
    console.log(err.appOrParcelName);
    console.log(err.stack);
    console.log(getAppStatus(err.appOrParcelName));
  });

  startSingleSpa();
} catch (error) {
  logger.error(error);
  history.push("/error");
}
